import { Component, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { filter, Observable } from 'rxjs';
import {
    EnedisAccessRightModel,
    EnedisConsumptionDetailModel,
    EnedisConsumptionModel,
    EnedisConsumptionStep,
    EnedisConsumptionStepLabel,
    EnedisConsumptionTypeSerie,
} from '../../../enedis.models';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import { EnedisState } from '../../../enedis.state';
import { EnedisService } from '../../../enedis.service';
import { AlertService, ButtonColors } from '@ed---interne/ng-uui-components';
import { ApexAxisChartSeries, ApexDataLabels, ApexYAxis } from 'ng-apexcharts';
import { convertNumber } from 'src/app/shared/all.helpers';

interface ConsumptionsTableElement extends Partial<EnedisConsumptionModel> {}

@UntilDestroy()
@Component({
    selector: 'app-enedis-meter-consumption',
    templateUrl: './enedis-meter-consumption.component.html',
    styleUrls: ['./enedis-meter-consumption.component.scss'],
})
export class EnedisMeterConsumptionComponent {
    public enedisAccessRight$: Observable<EnedisAccessRightModel> = inject(Store)
        .select(EnedisState.currentAccessRight)
        .pipe(filter(Boolean));
    public accessRight: EnedisAccessRightModel | null = null;

    public forceReload$: Observable<boolean> = inject(Store)
        .select(EnedisState.forceReload)
        .pipe(filter(Boolean));
    public forceReload: boolean = false;

    private now = new Date();
    dateStart: Date = new Date(this.now.getFullYear(), 0, this.now.getDate());
    dateEnd: Date = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate());
    type: EnedisConsumptionTypeSerie = EnedisConsumptionTypeSerie.FRN;
    step: EnedisConsumptionStep = EnedisConsumptionStep.MONTHLY;

    consumptions: ApexAxisChartSeries = [];
    loading = signal(false);
    allowReload = signal(false);

    typeOptions = Object.values(EnedisConsumptionTypeSerie).map((k) => ({ value: k, label: k }));
    stepOptions = Object.entries(EnedisConsumptionStepLabel).map(([k, v]) => ({
        value: k,
        label: v,
    }));

    consumptionsChartDataLabels: ApexDataLabels = { enabled: true, enabledOnSeries: [] };
    consumptionsChartYaxis: ApexYAxis = {
        min: 0,
        title: { text: 'Consommation (kWh)' },
        labels: {
            formatter: (value: number) => convertNumber(value),
        },
    };
    buttonColorsSecondary = ButtonColors.Secondary;
    buttonColorsPrimary = ButtonColors.Primary;

    public consumptionsDataSource: MatTableDataSource<ConsumptionsTableElement> =
        new MatTableDataSource<ConsumptionsTableElement>();
    public consumptionsDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.DATETIME,
            objectDisplayName: 'Début',
            objectKey: 'dateStart',
            bold: false,
        },
        {
            displayType: DisplayType.DATETIME,
            objectDisplayName: 'Fin',
            objectKey: 'dateEnd',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Classe',
            objectKey: 'temporalClass',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Consommation (kWh)',
            objectKey: 'consumptionkWh',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Statut',
            objectKey: 'statut',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Déclencheur',
            objectKey: 'declencheur',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nature',
            objectKey: 'nature',
            bold: false,
        },
    ];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly enedisService: EnedisService,
        private readonly alertService: AlertService,
        private readonly store: Store,
    ) {
        this.enedisAccessRight$.pipe(untilDestroyed(this)).subscribe((enedisAccessRight) => {
            this.accessRight = enedisAccessRight;
            this.fetchData();
        });
        this.forceReload$.pipe(untilDestroyed(this)).subscribe((forceReload: boolean) => {
            this.forceReload = forceReload;
            if (forceReload) {
                this.fetchData();
            }
        });
    }

    async updateType(type: EnedisConsumptionTypeSerie) {
        this.type = type;
        this.allowReload.set(true);
    }

    async updateStep(step: EnedisConsumptionStep) {
        if (step === EnedisConsumptionStep.DAILY) {
            const oneMonthRecommended = new Date(this.dateEnd);
            oneMonthRecommended.setMonth(oneMonthRecommended.getMonth() - 1);
            this.dateStart = new Date(
                Math.max(this.dateStart.getTime(), oneMonthRecommended.getTime()),
            );
        }
        this.step = step;
        this.allowReload.set(true);
    }
    async updateDateStart(dateStart: Date) {
        this.dateStart = dateStart;
        this.allowReload.set(true);
    }
    async updateDateEnd(dateEnd: Date) {
        this.dateEnd = new Date(Math.min(dateEnd.getTime(), new Date().getTime()));
        this.allowReload.set(true);
    }

    getSerieName(detail: EnedisConsumptionDetailModel): string {
        return `${detail.temporalClass} - ${detail.nature}`;
    }

    getSeriesName(entities: EnedisConsumptionModel[]): string[] {
        const set = new Set<string>();

        for (const entity of entities) {
            for (const detail of entity.consumptionDetail || []) {
                set.add(this.getSerieName(detail));
            }
        }

        return Array.from(set);
    }

    async fetchData(): Promise<void> {
        if (!this.dateStart || !this.dateEnd || !this.type || !this.accessRight) return;
        if (this.dateStart.getTime() >= this.dateEnd.getTime()) {
            this.alertService.warning(
                'Dates invalides',
                `La date de début doit être inférieure à la date de fin`,
            );
            return;
        }

        this.loading.set(true);
        this.consumptions = [];
        if (this.accessRight) {
            try {
                const data: EnedisConsumptionModel[] = await this.enedisService.getConsumptions(
                    this.accessRight.id,
                    this.dateStart,
                    this.dateEnd,
                    this.type,
                    this.step,
                    this.forceReload,
                );
                if (data.length == 0) {
                    this.alertService.warning('Consommation', 'Aucune donnée sur cette periode');
                    this.loading.set(false);
                    return;
                }

                const seriesName = this.getSeriesName(data);
                this.consumptions = seriesName.map((serieName) => ({
                    name: serieName,
                    type: 'bar',
                    data: data.map((d) => ({
                        x: d.dateStart,
                        y:
                            d.consumptionDetail.find((cd) => this.getSerieName(cd) === serieName)
                                ?.consumptionkWh || null,
                    })),
                }));
                this.consumptionsChartDataLabels.enabledOnSeries = this.consumptions.map(
                    (_, i) => i,
                );

                this.fillTable(data);

                this.allowReload.set(false);
                this.loading.set(false);
            } catch (error: any) {
                console.log(error);
                this.consumptions = [];
                this.loading.set(false);
                this.alertService.error(
                    'Erreur lors de la récupération des consommations',
                    `${error?.error?.message ?? 'Une erreur est survenue'}`,
                );
            }
        }
    }

    fillTable(data: EnedisConsumptionModel[]) {
        this.consumptionsDataSource = new MatTableDataSource<ConsumptionsTableElement>(
            data.reduce<any[]>((acc, parent) => {
                const { consumptionDetail, ...parentInfo } = parent;

                if (Array.isArray(consumptionDetail)) {
                    const flattened = consumptionDetail.map((detail) => ({
                        parentId: parent.id,
                        ...parentInfo,
                        detailId: detail.id,
                        ...detail,
                    }));

                    return acc.concat(flattened);
                }

                return acc;
            }, []),
        );
    }
}
